import { AnimatedSection } from '../components/AnimatedSection';
import { useMember } from '../context';

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'long',
  }).format(new Date(iso));
};

export const MemberProfilePage = () => {
  const { dashboard, dashboardLoading, dashboardError } = useMember();

  const user = dashboard?.user;

  if (dashboardLoading && !dashboard) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
            <p className="text-sm font-medium text-slate-500 dark:text-slate-300">
              Loading your profile...
            </p>
          </div>
        </div>
      </AnimatedSection>
    );
  }

  if (dashboardError) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
            <h2 className="font-display text-xl">Failed to load profile</h2>
            <p className="mt-2 text-sm">{dashboardError}</p>
          </div>
        </div>
      </AnimatedSection>
    );
  }

  if (!dashboard || !user) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-center py-20">
          <p className="text-sm text-slate-500 dark:text-slate-300">No profile data available.</p>
        </div>
      </AnimatedSection>
    );
  }

  return (
  <AnimatedSection className="section-padding">
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-10">
      <header className="rounded-3xl border border-slate-200 bg-white px-8 py-10 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-brand-700 dark:text-brand-300">Profile</p>
            <h1 className="mt-3 font-display text-3xl text-slate-900 dark:text-white">{user.full_name}</h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Member ID: {user.id}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Joined {dashboard.chama_config.name} · {formatDate(user.date_joined)}
            </p>
          </div>
          <nav className="flex flex-wrap gap-4 text-sm font-semibold text-brand-600 dark:text-brand-300">
            <button className="text-slate-600 transition hover:text-brand-600 dark:text-slate-300 dark:hover:text-brand-200">
              Edit details
            </button>
          </nav>
        </div>
      </header>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <h2 className="font-display text-lg text-slate-900 dark:text-white">Contact information</h2>
          <dl className="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
            <div>
              <dt className="text-xs uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500">Phone</dt>
              <dd className="mt-1 font-semibold text-slate-900 dark:text-white">{user.phone}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500">Email</dt>
              <dd className="mt-1 font-semibold text-slate-900 dark:text-white">
                {user.email ?? 'Not provided'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500">National ID</dt>
              <dd className="mt-1 font-semibold text-slate-900 dark:text-white">{user.id_number}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <h2 className="font-display text-lg text-slate-900 dark:text-white">Account status</h2>
          <ul className="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
            <li className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
              <span>Member status</span>
              <span className="text-sm font-semibold text-brand-600 dark:text-brand-300">
                {user.status}
              </span>
            </li>
            <li className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
              <span>Date joined</span>
              <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                {formatDate(user.date_joined)}
              </span>
            </li>
            <li className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
              <span>Current cycle</span>
              <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                Cycle {dashboard.chama_config.current_cycle}
              </span>
            </li>
          </ul>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <h2 className="font-display text-lg text-slate-900 dark:text-white">Contribution details</h2>
          <ul className="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
            <li className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
              <p className="font-semibold text-slate-900 dark:text-white">Monthly contribution</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                KES {dashboard.chama_config.contribution_amount.toLocaleString()}
              </p>
            </li>
            <li className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
              <p className="font-semibold text-slate-900 dark:text-white">Contribution window</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Day {dashboard.chama_config.contribution_start} - {dashboard.chama_config.contribution_end} of each
                month
              </p>
            </li>
            <li className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
              <p className="font-semibold text-slate-900 dark:text-white">Penalty per day</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                KES {dashboard.chama_config.penalty_per_day.toLocaleString()}
              </p>
            </li>
          </ul>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white px-8 py-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="font-display text-xl text-slate-900 dark:text-white">Member note</h2>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
          Welcome to {dashboard.chama_config.name}! You joined on {formatDate(user.date_joined)} and are currently in Cycle {dashboard.chama_config.current_cycle}.
          Keep up with your monthly contributions to maintain good standing.
        </p>
      </section>
    </div>
  </AnimatedSection>
  );
};
